/**
 * Timezone Utilities for Trinidad and Tobago
 *
 * Trinidad and Tobago timezone: America/Port_of_Spain (UTC-4, AST - Atlantic Standard Time)
 * No daylight saving time is observed.
 */
export const TRINIDAD_TIMEZONE = 'America/Port_of_Spain';
export const TRINIDAD_UTC_OFFSET = -4; // UTC-4 hours
/**
 * Get current date/time in Trinidad and Tobago timezone
 */
export function getTrinidadTime() {
    // Create date in Trinidad timezone
    const now = new Date();
    const trinidadTime = new Date(now.toLocaleString('en-US', { timeZone: TRINIDAD_TIMEZONE }));
    return trinidadTime;
}
/**
 * Get current date/time as ISO string in Trinidad timezone
 */
export function getTrinidadTimeISO() {
    return getTrinidadTime().toISOString();
}
/**
 * Convert any date to Trinidad timezone
 */
export function toTrinidadTime(date) {
    const inputDate = typeof date === 'string' ? new Date(date) : date;
    return new Date(inputDate.toLocaleString('en-US', { timeZone: TRINIDAD_TIMEZONE }));
}
/**
 * Get current day name in Trinidad timezone (lowercase)
 * Returns: 'monday', 'tuesday', etc.
 */
export function getTrinidadDayName() {
    const trinidadTime = getTrinidadTime();
    return trinidadTime.toLocaleDateString('en-US', {
        weekday: 'long',
        timeZone: TRINIDAD_TIMEZONE
    }).toLowerCase();
}
/**
 * Get current time string in HH:MM format in Trinidad timezone
 */
export function getTrinidadTimeString() {
    const trinidadTime = getTrinidadTime();
    return trinidadTime.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        timeZone: TRINIDAD_TIMEZONE
    });
}
/**
 * Format date for Trinidad timezone display
 */
export function formatTrinidadDate(date, options) {
    const inputDate = typeof date === 'string' ? new Date(date) : date;
    return inputDate.toLocaleDateString('en-US', {
        ...options,
        timeZone: TRINIDAD_TIMEZONE
    });
}
/**
 * Format time for Trinidad timezone display
 */
export function formatTrinidadTime(date, options) {
    const inputDate = typeof date === 'string' ? new Date(date) : date;
    return inputDate.toLocaleTimeString('en-US', {
        ...options,
        timeZone: TRINIDAD_TIMEZONE
    });
}
/**
 * Get start of day in Trinidad timezone as ISO string
 */
export function getTrinidadDayStart(date) {
    const inputDate = date ? (typeof date === 'string' ? new Date(date) : date) : getTrinidadTime();
    const trinidadDate = toTrinidadTime(inputDate);
    // Set to start of day (00:00:00)
    trinidadDate.setHours(0, 0, 0, 0);
    return trinidadDate.toISOString();
}
/**
 * Get end of day in Trinidad timezone as ISO string
 */
export function getTrinidadDayEnd(date) {
    const inputDate = date ? (typeof date === 'string' ? new Date(date) : date) : getTrinidadTime();
    const trinidadDate = toTrinidadTime(inputDate);
    // Set to end of day (23:59:59.999)
    trinidadDate.setHours(23, 59, 59, 999);
    return trinidadDate.toISOString();
}
/**
 * Get date in YYYY-MM-DD format for Trinidad timezone
 */
export function getTrinidadDateString(date) {
    const inputDate = date ? (typeof date === 'string' ? new Date(date) : date) : getTrinidadTime();
    return formatTrinidadDate(inputDate, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).split('/').reverse().join('-'); // Convert MM/DD/YYYY to YYYY-MM-DD
}
/**
 * Check if a date is today in Trinidad timezone
 */
export function isTrinidadToday(date) {
    const inputDate = typeof date === 'string' ? new Date(date) : date;
    const todayString = getTrinidadDateString();
    const dateString = getTrinidadDateString(inputDate);
    return todayString === dateString;
}
/**
 * Get start of Trinidad day as a true UTC Date — safe for backend use.
 *
 * The existing getTrinidadDayStart() uses the toLocaleString() trick which
 * interprets the result in the *process* local timezone. On a server whose
 * TZ env is UTC that works, but it breaks in any other server timezone.
 *
 * This function uses Intl.DateTimeFormat ('sv' locale gives YYYY-MM-DD) to
 * read the TT calendar date, then builds the UTC equivalent directly.
 * TT is permanently UTC-4 with no DST, so midnight TT = 04:00 UTC.
 */
export function getTrinidadDayStartUTC(date) {
    const d = date ?? new Date();
    const ttDateStr = new Intl.DateTimeFormat('sv', { timeZone: TRINIDAD_TIMEZONE }).format(d);
    const [year, month, day] = ttDateStr.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day, 4, 0, 0, 0)); // TT 00:00 = UTC 04:00
}
/**
 * Get end of Trinidad day as a true UTC Date — safe for backend use.
 * Returns 23:59:59.999 TT = 03:59:59.999 UTC of the following day.
 */
export function getTrinidadDayEndUTC(date) {
    return new Date(getTrinidadDayStartUTC(date).getTime() + 24 * 60 * 60 * 1000 - 1);
}
/**
 * Get the current Trinidad calendar date as a YYYY-MM-DD string.
 * Safe for both browser and backend — uses Intl.DateTimeFormat directly.
 */
export function getTrinidadDateStringUTC(date) {
    return new Intl.DateTimeFormat('sv', { timeZone: TRINIDAD_TIMEZONE }).format(date ?? new Date());
}
/**
 * Get time difference in minutes between two dates
 */
export function getMinutesDifference(date1, date2) {
    const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
    const d2 = typeof date2 === 'string' ? new Date(date2) : date2;
    return Math.floor((d2.getTime() - d1.getTime()) / (1000 * 60));
}
/**
 * Create a new Date object representing current Trinidad time
 * This is useful for database operations that need current timestamp
 */
export function createTrinidadTimestamp() {
    return getTrinidadTime();
}
//# sourceMappingURL=timezone.js.map